import { CommonFooter } from "@faire/retailer-visitor-shared/components/AppSignIn/components/__internal__/EmailSentWaitingViewHelper";
import { AllResult } from "@faire/retailer-visitor-shared/components/AppSignIn/components/SignInController";
import { LoginWrapper } from "@faire/retailer-visitor-shared/components/AppSignIn/components/styled";
import { trackSigninCheckEmailView } from "@faire/web-api--source/events/signin/view/checkEmail";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import * as React from "react";
import { useEffect } from "react";

interface IEmailSentWaitingViewProps {
  scenario: EmailSentScenario;
  image?: string;
  title?: string;
  subtitle?: string;
  footer?: React.ReactNode;
}

export enum EmailSentScenario {
  AUTO = "AUTO",
  MFA = "MFA",
  MFA_OPTIONS = "MFA_OPTIONS",
  MFA_OPTIONS_EMAIL_SENT = "MFA_OPTIONS_EMAIL_SENT",
}

const AUTO_IMAGE =
  "https://cdn.faire.com/fastly/156f191b1cee0a924570a6b23d7d8c7c35d78bfa643d72f9a007fb030757cb32.png";

const MFA_EMAIL_SENT_IMAGE =
  "https://cdn.faire.com/fastly/156f191b1cee0a924570a6b23d7d8c7c35d78bfa643d72f9a007fb030757cb32.png";

const EmailSentWaitingViewBase: React.FC<IEmailSentWaitingViewProps> = ({
  title,
  subtitle,
  footer,
  image,
  scenario,
}) => {
  useEffect(() => {
    trackSigninCheckEmailView(scenario.toLowerCase());
  }, [scenario]);
  return (
    <LoginWrapper noSpacing>
      <img
        src={image}
        width="100%"
        data-test-id={`email-sent-waiting-img-${scenario}`}
      />
      <Typography variant="displaySSerifRegular">{title}</Typography>
      <Spacer size="1x" />
      <Typography>{subtitle}</Typography>
      <Spacer size="3x" />
      {footer}
      <Spacer
        size={{
          mobileAndAbove: "3x",
          tabletAndAbove: 0,
        }}
      />
    </LoginWrapper>
  );
};

export const AutoEmailSentWaitingView: React.FC<{
  scenario: EmailSentScenario | undefined;
  email: string;
  onComplete: (result: AllResult) => void;
}> = ({ onComplete, scenario = EmailSentScenario.MFA, email }) => {
  const { strictLocalize } = useStrictLocalization();

  useEffect(() => {
    trackSigninCheckEmailView(scenario.toLowerCase());
  }, [scenario]);

  // If not AUTO scenario, return null or handle other scenarios
  if (scenario !== EmailSentScenario.AUTO) {
    return null;
  }

  return (
    <EmailSentWaitingViewBase
      title={strictLocalize({
        id: "dCsj66",
        defaultMessage: "We emailed you a link to help you sign back in",
        description: {
          text: "Title for telling the user to check their emails",
          img: "https://cdn.faire.com/fastly/662920f995541979615646df5b8accbbb555fe27e567e84001666ec59ac69877.png",
        },
      })}
      subtitle={strictLocalize(
        {
          id: "+MZnyx",
          defaultMessage:
            "Check {EmailFullyTranslatedSentence} for a link to sign in without a password.",
          description: {
            text: "Description for telling the user that a magic link has been sent to their email",
            img: "https://cdn.faire.com/fastly/662920f995541979615646df5b8accbbb555fe27e567e84001666ec59ac69877.png",
          },
        },
        { EmailFullyTranslatedSentence: email }
      )}
      image={AUTO_IMAGE}
      scenario={EmailSentScenario.AUTO}
      footer={
        <CommonFooter
          onComplete={onComplete}
          email={email}
          scenario={scenario}
        />
      }
    />
  );
};

export const MFAOptionsEmailSentWaitingView: React.FC<{
  email?: string;
  onComplete: (result: AllResult) => void;
  mfaTitle?: string;
  mfaDescription?: string;
  password?: string;
}> = ({ onComplete, email, mfaTitle, mfaDescription, password }) => {
  return (
    <EmailSentWaitingViewBase
      title={mfaTitle}
      subtitle={mfaDescription}
      image={MFA_EMAIL_SENT_IMAGE}
      scenario={EmailSentScenario.MFA_OPTIONS_EMAIL_SENT}
      footer={
        <CommonFooter
          onComplete={onComplete}
          email={email ?? ""}
          scenario={EmailSentScenario.MFA_OPTIONS_EMAIL_SENT}
          password={password}
        />
      }
    />
  );
};
